import { VoucherResponse } from '@/app/types/voucher'
import { Party } from '@/app/types/party'
import { ThermalPrinterService, ThermalPrinterConfig, CompanyDetails } from '@/app/services/thermalPrinterService'
import { ReceiptTemplate, getTemplateForBusiness } from '@/app/config/receiptTemplates'
import { PartyClosingBalanceResponse } from '@/app/types/reports'

export interface ThermalPrintOptions {
  preview: boolean
  printerName?: string
  copies?: number
  pageSize?: '80mm' | '58mm' | '44mm'
  silent?: boolean
  printerType?: 'epson' | 'star' | 'tsc' | 'generic'
  template?: string | ReceiptTemplate
  useEscPos?: boolean
  includeBarcode?: boolean
  includeQrCode?: boolean
  charactersPerLine?: number
}

// Create thermal printer instance
export function createThermalPrinter(options: ThermalPrintOptions): ThermalPrinterService {
  // Get template configuration
  let template: ReceiptTemplate | undefined
  if (typeof options.template === 'string') {
    template = getTemplateForBusiness(options.template)
  } else if (options.template) {
    template = options.template
  } else {
    template = getTemplateForBusiness('retail')
  }
  
  const config: ThermalPrinterConfig = {
    paperSize: options.pageSize || template?.config.formatting.paperSize || '80mm',
    printerType: options.printerType || 'generic',
    characterSet: 'pc437',
    interface: 'usb',
    useEscPos: options.useEscPos ?? template?.config.formatting.useEscPos ?? true,
    includeBarcode: options.includeBarcode ?? template?.config.footer.showBarcode ?? false,
    includeQrCode: options.includeQrCode ?? template?.config.footer.showQrCode ?? false,
    charactersPerLine: options.charactersPerLine
  }
  
  return new ThermalPrinterService(config)
}

// Generate receipt data using the thermal printer service
export async function formatInvoiceForThermalPrinter(
  invoice: VoucherResponse,
  party: Party | undefined,
  companyDetails: CompanyDetails,
  options: ThermalPrintOptions,
  closingBalance?: PartyClosingBalanceResponse
): Promise<{
  escPosData: Uint8Array
  textReceipt: string
}> {
  const printer = createThermalPrinter(options)
  
  // Generate ESC/POS commands
  const escPosData = await printer.generateInvoiceReceipt(invoice, party, companyDetails, closingBalance)
  
  // Generate text receipt for preview
  const textReceipt = printer.generateTextReceipt(invoice, party, companyDetails, closingBalance)
  
  return { escPosData, textReceipt }
}

// Print the receipt
export async function printThermalReceipt(
  invoice: VoucherResponse,
  party: Party | undefined,
  companyDetails: CompanyDetails,
  options: ThermalPrintOptions,
  closingBalance?: PartyClosingBalanceResponse
): Promise<boolean> {
  try {
    const printer = createThermalPrinter(options)
    const { escPosData, textReceipt } = await formatInvoiceForThermalPrinter(
      invoice,
      party,
      companyDetails,
      options,
      closingBalance
    )
    
    // Try USB printing first
    const usbPrintSuccess = await printer.printViaUSB(escPosData)
    
    if (!usbPrintSuccess) {
      // Fallback to window.print
      printer.printViaWindowPrint(textReceipt, options.pageSize || '80mm')
    }
    
    return true
  } catch (error) {
    console.error('Error printing thermal receipt:', error)
    return false
  }
}

// Helper function to get default thermal print options
export function getDefaultThermalPrintOptions(): ThermalPrintOptions {
  return {
    preview: false,
    copies: 1,
    pageSize: '80mm',
    silent: false,
    printerType: 'generic',
    template: 'retail',
    useEscPos: false,
    includeBarcode: false,
    includeQrCode: false
  }
}

// Get thermal print options for specific business type
export function getThermalPrintOptionsForBusiness(businessType: string): ThermalPrintOptions {
  const template = getTemplateForBusiness(businessType)
  return {
    preview: false,
    copies: 1,
    pageSize: template.config.formatting.paperSize,
    silent: false,
    printerType: 'generic',
    template: template.id,
    useEscPos: template.config.formatting.useEscPos,
    includeBarcode: template.config.footer.showBarcode,
    includeQrCode: template.config.footer.showQrCode
  }
}